/* istanbul ignore file */

import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { BrannanNavigation } from '@graphql/generated-base';
import type { BoxProps } from '@withjoy/joykit';
import { Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import React, { useEffect, useRef, useMemo } from 'react';
import { useImmer } from 'use-immer';
import { NavLink as RouterLink } from '@react-router';
import { getHoverColor, isSupportedEventPage } from '../../../layout.utils';
import { StyledLegacyNavLinkContainer } from './NavBar.styles';
import { TopBarNavEventMenu } from '@apps/guest/packages/layout-engine/components/TopBarNavEventMenu';
import { PreviewDisabledTooltipContent } from '@apps/guest/packages/layout-engine/components/PreviewDisabledTooltipContent/PreviewDisabledTooltipContent';
import { usePreviewIframeScale } from '@apps/guest/packages/layout-engine/components/PreviewDisabledTooltipContent/usePreviewIframeScale';
import type { NavbarEventPropsInterface } from '../../../layout.types';
import { formatPagesForBrannan } from './NavBar.utils';
import { getNativeRegistryHardNavUrl } from '../../../layout.utils';
import { isInIframe } from '@shared/utils/isInIframe';
import { useEventPageRoute } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import useQueryString from '@shared/utils/hooks/useQueryString';
import { useDefaultPage } from '@shared/hooks/useDefaultPage';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body1ToNavigationOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { pxToRem } from '@withjoy/joykit/theme';

export interface NavBarProps
  extends Readonly<{
      eventHandle: string;
      pages: ReadonlyArray<EventPageFragment>;
      variant: 'inline' | 'menu';
      title?: Maybe<string>;
      isCustomPage?: boolean;
      handleMenuClicked?: (item: string) => void;
      textColor?: string;
      /**
       * When true, the nav will expand to full width.
       * Used in StickyNavBar component
       */
      expandToFullWidth?: boolean;
      /**
       * When true, the nav will not expand to full width even if the page blends seamlessly with the art background.
       * Used in EventNameNavForStandaloneHeader component
       */
      preventExpandingToFullWidth?: boolean;
      /**
       * Optional initial maxWidth value to prevent layout shift during initial render.
       * If not provided, maxWidth starts as undefined and is calculated after mount.
       */
      initialMaxWidth?: number;
    }>,
    Omit<BoxProps, 'title'> {}

const LEGACY_PAGE_SLUGS = new Set(['rsvp', 'moments']);

const LegacyNavLink: React.FC<{
  to: string;
  isRsvp: boolean;
  isNativeRegistry: boolean;
  isLegacyPage: boolean;
  isPreviewing: boolean;
  previewScale?: number;
  onClick: () => void;
  colorOverride?: string;
  isDefaultPage?: boolean;
}> = ({ to, isRsvp, isNativeRegistry, isLegacyPage, isPreviewing, previewScale, children, onClick, colorOverride, isDefaultPage, ...rest }) => {
  const { pageFontColor } = useColorPaletteProvider();
  const linkColor = useMemo(() => colorOverride || pageFontColor, [colorOverride, pageFontColor]);
  const linkHoverColor = useMemo(() => (linkColor ? getHoverColor(linkColor) : undefined), [linkColor]);

  if (isLegacyPage && isPreviewing) {
    return (
      <StyledLegacyNavLinkContainer
        _empty={{ display: 'none' }}
        paddingX={5}
        linkColor={linkColor}
        linkHoverColor={linkHoverColor}
        data-testid={(rest as Record<string, string>)['data-testid']}
      >
        <TooltipV2
          onMouseEnterDelay={500}
          content={
            <PreviewDisabledTooltipContent
              href={to}
              previewScale={previewScale}
            />
          }
        >
          <TextV2
            cursor="default"
            color={linkColor}
          >
            {children}
          </TextV2>
        </TooltipV2>
      </StyledLegacyNavLinkContainer>
    );
  }

  return (
    <StyledLegacyNavLinkContainer
      _empty={{ display: 'none' }}
      paddingX={5}
      linkColor={linkColor}
      linkHoverColor={linkHoverColor}
    >
      {isRsvp ? (
        <a href={to}>{children}</a>
      ) : isNativeRegistry ? (
        <a
          onClick={onClick}
          href={to}
          data-testid={(rest as Record<string, string>)['data-testid']}
        >
          {children}
        </a>
      ) : (
        <RouterLink
          onClick={onClick}
          data-testid={(rest as Record<string, string>)['data-testid']}
          to={to}
          exact={isDefaultPage}
        >
          {children}
        </RouterLink>
      )}
    </StyledLegacyNavLinkContainer>
  );
};

const OVERFLOW_BOUND = 860;
const DEFAULT_PADDING = pxToRem(12);
const FULL_WIDTH = '100vw';
const FULL_WIDTH_PADDING = pxToRem(64);

type OverflowState = { maxWidth: number | undefined; hasOverflow: boolean };

const LegacyBrannanOverflowNav: React.FC<Omit<NavBarProps, 'variant' | 'title'> & { isSinglePage: boolean }> = ({
  pages,
  eventHandle,
  handleMenuClicked,
  backgroundColor,
  textColor,
  paddingTop = 8,
  borderRadius = 0,
  backdropFilter = 'none',
  border = 'none',
  boxShadow,
  expandToFullWidth,
  preventExpandingToFullWidth,
  initialMaxWidth,
  isSinglePage,
  ...restProps
}) => {
  const containerRef = useRef<HTMLUListElement | null>(null);
  const [state, setState] = useImmer<OverflowState>({
    maxWidth: initialMaxWidth ?? undefined,
    hasOverflow: false,
  });
  const eventPageRoute = useEventPageRoute(eventHandle, pages);
  const queryString = useQueryString();
  const [useRegistryLinksNav, isRegistryLinksNavLoading] = useIsFeatureEnabled('registryEnableGuestNavRegistryLinks');
  const { isDefaultPage } = useDefaultPage(pages);
  const { pageColor, artBackgroundColor, brannanInnerFrameBorder, themeId, isCustomArtApplied } = useColorPaletteProvider();

  // Expand nav to full viewport width (FULL_WIDTH) with FULL_WIDTH_PADDING padding when:
  const shouldExpandNav = useMemo(() => {
    if (preventExpandingToFullWidth) {
      return false;
    }

    // When the expand to full width flag is enabled (used in the StickyNavBar component)
    if (expandToFullWidth) {
      return true;
    }

    // When the page blends seamlessly with the art background (matching colors, blank theme, no shadow).
    // Custom theme might have colors and may not blend well.
    return pageColor === artBackgroundColor && themeId === 'blank' && !brannanInnerFrameBorder && !isCustomArtApplied;
  }, [pageColor, artBackgroundColor, themeId, brannanInnerFrameBorder, expandToFullWidth, preventExpandingToFullWidth, isCustomArtApplied]);

  const isPreviewing = isInIframe();
  const previewScale = usePreviewIframeScale(isPreviewing);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const _checkOverflow = () => {
        if (container.childElementCount > 0) {
          const results = Array.from(container.children).reduce(
            (acc, child, idx) => {
              if (!acc.hasOverflow) {
                const width = child.getBoundingClientRect().width + 32;
                const hasOverflow = acc.runningWidth + width >= OVERFLOW_BOUND;
                if (!hasOverflow) {
                  acc.runningWidth += width;
                  acc.lastIndex = idx;
                }
                acc.hasOverflow = hasOverflow;
              }

              return acc;
            },
            { runningWidth: 0, lastIndex: 0, hasOverflow: false } as { runningWidth: number; lastIndex: number; hasOverflow: boolean },
          );

          // The last child's offset top will be larger when overflow
          setState(draft => {
            draft.hasOverflow = results.hasOverflow;
            draft.maxWidth = results.hasOverflow ? results.runningWidth : undefined;
          });
        } else {
          setState(draft => {
            draft.hasOverflow = false;
            draft.maxWidth = undefined;
          });
        }
      };

      _checkOverflow();
      const observer = new MutationObserver(() => {
        _checkOverflow();
      });
      observer.observe(container, { childList: true, subtree: true, characterData: true });
    }
  }, [pages, setState]);

  return (
    <Flex
      as="nav"
      {...restProps}
      justifyContent="center"
      paddingTop={paddingTop}
      pointerEvents={isPreviewing ? 'all' : 'unset'}
    >
      <Flex
        as="ul"
        flexWrap={'wrap'}
        maxWidth={shouldExpandNav ? FULL_WIDTH : state.hasOverflow ? '90%' : OVERFLOW_BOUND}
        width={shouldExpandNav ? FULL_WIDTH : undefined}
        minWidth={shouldExpandNav ? FULL_WIDTH : undefined}
        justifyContent="center"
        ref={containerRef}
        backgroundColor={backgroundColor}
        borderRadius={borderRadius}
        border={border}
        backdropFilter={backdropFilter}
        boxShadow={boxShadow}
        paddingX={shouldExpandNav ? FULL_WIDTH_PADDING : DEFAULT_PADDING}
      >
        {pages.map(page => {
          if (!isSupportedEventPage(page.type)) {
            return null;
          }

          const isRsvp = page.pageSlug === 'rsvp';
          // While the remote flag is resolving, bias toward the SPA path (skip the hard-nav
          // helper) so a fast click on treatment users doesn't land them in /registry.
          const registryHardNavUrl =
            useRegistryLinksNav || isRegistryLinksNavLoading
              ? undefined
              : getNativeRegistryHardNavUrl(page.type, page.pageSlug, page.pageKind, isSinglePage, eventHandle, queryString);
          const isNativeRegistry = !!registryHardNavUrl;
          const isLegacyPage = LEGACY_PAGE_SLUGS.has(page.pageSlug);
          const to = registryHardNavUrl ?? eventPageRoute.goToPath(page.type, page.pageSlug);

          return (
            <LegacyNavLink
              onClick={() => handleMenuClicked && handleMenuClicked(page.pageSlug)}
              key={page.id}
              data-testid={`nav-${page.id}`}
              isLegacyPage={isLegacyPage}
              isPreviewing={isPreviewing}
              previewScale={previewScale}
              isRsvp={isRsvp}
              isNativeRegistry={isNativeRegistry}
              to={to}
              colorOverride={textColor}
              isDefaultPage={isDefaultPage(page.pageSlug, page.type)}
            >
              <GuestSiteTypographyOverride override={body1ToNavigationOverride}>
                <TextV2
                  typographyVariant="body1"
                  paddingTop={5}
                  paddingBottom={5}
                >
                  {page.pageTitle}
                </TextV2>
              </GuestSiteTypographyOverride>
            </LegacyNavLink>
          );
        })}
      </Flex>
    </Flex>
  );
};

const NavBar: React.FC<NavBarProps & { eventProps: NavbarEventPropsInterface }> = ({
  eventProps,
  eventHandle,
  pages: origPages,
  variant,
  title,
  isCustomPage,
  handleMenuClicked,
  ...restProps
}) => {
  const isVariantMenu = variant === 'menu';
  const isVariantInline = variant === 'inline';
  const isSinglePage = eventProps.brannanNavigation === BrannanNavigation.singlePage;
  const pages = useMemo(() => (isSinglePage ? origPages : formatPagesForBrannan(origPages)), [origPages, isSinglePage]);
  const { pageColor } = useColorPaletteProvider();

  const eventPropsFormatted = useMemo(
    () => ({
      ...eventProps,
      pages: isSinglePage ? eventProps.pages : formatPagesForBrannan(eventProps.pages),
    }),
    [eventProps, isSinglePage],
  );

  return (
    <>
      {isVariantMenu && (
        <TopBarNavEventMenu
          handleMenuClicked={handleMenuClicked}
          eventHandle={eventHandle}
          title={title}
          eventProps={eventPropsFormatted}
          isCustomPage={isCustomPage}
          backgroundColor={pageColor}
          isSinglePage={isSinglePage}
        />
      )}
      {isVariantInline && (
        <LegacyBrannanOverflowNav
          handleMenuClicked={handleMenuClicked}
          pages={pages}
          eventHandle={eventHandle}
          isSinglePage={isSinglePage}
          {...restProps}
        />
      )}
    </>
  );
};

NavBar.displayName = 'NavBar';

export { NavBar };
